import { useCallback, useEffect, useRef, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { useProjectStore } from '../../../store/projectStore';
import { useLiveFloorPlanStore } from '../../../store/liveFloorPlanStore';
import { DesignFloorPlan, FloorPlanPoint } from '../../../types';
import {
  collectUniqueEndpoints,
  findClosedLoopAtPoint,
  findNearestWall,
  lengthToMeters,
  pointInPolygon,
  snapDraftEndpoint,
  snapPointDetailed,
  snapDraftPointToGrid,
  snapDragDeltaToGrid,
  applyConnectedWallDragToPlan,
  applyWallLengthToPlan,
  WALL_DRAG_THRESHOLD_PX,
  DraftSnapResult,
  ENDPOINT_SNAP_PX,
  DEFAULT_DOOR_WIDTH_M,
  DEFAULT_PIXELS_PER_METER,
  clampDoorT,
  doorFitsOnWall,
  nextDoorSwing,
  DoorHinge,
  DoorSwingSide,
} from '../../../utils/floorPlanGeometry';

interface UseFloorPlanInteractionArgs {
  getCanvasCoordinates: (clientX: number, clientY: number) => FloorPlanPoint;
  /** Canvas zoom — snap distances are enlarged when zoomed out */
  canvasZoom?: number;
}

function wallsSnapToGridEnabled(): { enabled: boolean; gridSize: number } {
  const { snapToGrid, snapToGridTarget, gridSize } = useProjectStore.getState();
  return {
    enabled: !!(snapToGrid && (snapToGridTarget === 'walls' || snapToGridTarget === 'both') && gridSize > 0),
    gridSize,
  };
}

function maybeSnapDragDelta(
  session: {
    mode: 'push' | 'corner';
    wallId: string;
    vertexPoint?: FloorPlanPoint;
    startPlan: DesignFloorPlan;
  },
  delta: FloorPlanPoint
): FloorPlanPoint {
  const { enabled, gridSize } = wallsSnapToGridEnabled();
  if (!enabled) return delta;
  const wall = session.startPlan.walls.find((w) => w.id === session.wallId) || null;
  return snapDragDeltaToGrid({
    mode: session.mode,
    wall,
    vertexPoint: session.vertexPoint,
    delta,
    gridSize,
  });
}

/**
 * Hook that owns wall/room/door/measure drafting state and keyboard shortcuts.
 */
export function useFloorPlanInteraction({
  getCanvasCoordinates,
  canvasZoom = 1,
}: UseFloorPlanInteractionArgs) {
  const {
    canvasMode,
    setCanvasMode,
    getFloorPlan,
    ensureFloorPlan,
    addWall,
    addOpening,
    updateOpening,
    addRoom,
    updateRoom,
    deleteWall,
    deleteWalls,
    deleteRoom,
    deleteOpening,
    selectedFloorPlanWallId,
    selectedFloorPlanWallIds,
    selectedFloorPlanRoomId,
    selectedFloorPlanOpeningId,
    selectFloorPlanWall,
    setSelectedFloorPlanRoomId,
    setSelectedFloorPlanOpeningId,
    undoFloorPlan,
    redoFloorPlan,
    migrateLegacyFloorplanIntoDesign,
    snapshotFloorPlanForDrag,
    applyFloorPlanLive,
    discardLastFloorPlanSnapshot,
    wallDragMode,
  } = useProjectStore(
    useShallow((s) => ({
      canvasMode: s.canvasMode,
      setCanvasMode: s.setCanvasMode,
      getFloorPlan: s.getFloorPlan,
      ensureFloorPlan: s.ensureFloorPlan,
      addWall: s.addWall,
      addOpening: s.addOpening,
      updateOpening: s.updateOpening,
      addRoom: s.addRoom,
      updateRoom: s.updateRoom,
      deleteWall: s.deleteWall,
      deleteWalls: s.deleteWalls,
      deleteRoom: s.deleteRoom,
      deleteOpening: s.deleteOpening,
      selectedFloorPlanWallId: s.selectedFloorPlanWallId,
      selectedFloorPlanWallIds: s.selectedFloorPlanWallIds,
      selectedFloorPlanRoomId: s.selectedFloorPlanRoomId,
      selectedFloorPlanOpeningId: s.selectedFloorPlanOpeningId,
      selectFloorPlanWall: s.selectFloorPlanWall,
      setSelectedFloorPlanRoomId: s.setSelectedFloorPlanRoomId,
      setSelectedFloorPlanOpeningId: s.setSelectedFloorPlanOpeningId,
      undoFloorPlan: s.undoFloorPlan,
      redoFloorPlan: s.redoFloorPlan,
      migrateLegacyFloorplanIntoDesign: s.migrateLegacyFloorplanIntoDesign,
      snapshotFloorPlanForDrag: s.snapshotFloorPlanForDrag,
      applyFloorPlanLive: s.applyFloorPlanLive,
      discardLastFloorPlanSnapshot: s.discardLastFloorPlanSnapshot,
      wallDragMode: s.wallDragMode,
    }))
  );

  const endpointSnapPx = Math.max(ENDPOINT_SNAP_PX, ENDPOINT_SNAP_PX / Math.max(canvasZoom, 0.25));

  const [draftStart, setDraftStart] = useState<FloorPlanPoint | null>(null);
  const [draftCurrent, setDraftCurrent] = useState<FloorPlanPoint | null>(null);
  const [draftSnap, setDraftSnap] = useState<DraftSnapResult | null>(null);
  /** Nearby endpoints shown as magnets while in wall mode */
  const [snapMagnets, setSnapMagnets] = useState<FloorPlanPoint[]>([]);
  const [measureLocked, setMeasureLocked] = useState(false);
  const [lengthInput, setLengthInput] = useState('');
  const [altFreeAngle, setAltFreeAngle] = useState(false);
  const [roomNamePrompt, setRoomNamePrompt] = useState<{
    mode: 'create' | 'rename';
    roomId?: string;
    polygon: FloorPlanPoint[];
    wallIds: string[];
  } | null>(null);
  const [roomName, setRoomName] = useState('');
  /** Live door placement preview while hovering a wall in door mode */
  const [doorPreview, setDoorPreview] = useState<{
    wallId: string;
    t: number;
    widthM: number;
    fits: boolean;
    hinge: DoorHinge;
    swingSide: DoorSwingSide;
  } | null>(null);
  /** Current door swing orientation for placement (cycle with R in door mode) */
  const [doorSwing, setDoorSwing] = useState<{ hinge: DoorHinge; swingSide: DoorSwingSide }>({
    hinge: 'start',
    swingSide: 1,
  });

  type WallDragSession = {
    mode: 'push' | 'corner';
    wallId: string;
    vertexPoint?: FloorPlanPoint;
    origin: FloorPlanPoint;
    startPlan: DesignFloorPlan;
    started: boolean;
    lastDelta: FloorPlanPoint;
    lastFreeAngle: boolean;
  };
  const wallDragRef = useRef<WallDragSession | null>(null);
  const skipNextWallClickRef = useRef(false);
  const livePlanRafRef = useRef(0);
  const [isWallDragActive, setIsWallDragActive] = useState(false);

  const handleWallPointerDown = useCallback(
    (
      _event: React.PointerEvent,
      info: { mode: 'push' | 'corner'; wallId: string; vertexPoint?: FloorPlanPoint }
    ) => {
      if (canvasMode !== 'select') return;
      const fp = getFloorPlan();
      if (!fp) return;
      const origin = getCanvasCoordinates(_event.clientX, _event.clientY);
      wallDragRef.current = {
        mode: info.mode,
        wallId: info.wallId,
        vertexPoint: info.vertexPoint,
        origin,
        startPlan: JSON.parse(JSON.stringify(fp)) as DesignFloorPlan,
        started: false,
        lastDelta: { x: 0, y: 0 },
        lastFreeAngle: false,
      };
      skipNextWallClickRef.current = false;
    },
    [canvasMode, getCanvasCoordinates, getFloorPlan]
  );

  const consumeWallDragClick = useCallback(() => {
    if (!skipNextWallClickRef.current) return false;
    skipNextWallClickRef.current = false;
    return true;
  }, []);

  useEffect(() => {
    const onMove = (e: PointerEvent) => {
      const session = wallDragRef.current;
      if (!session) return;
      const raw = getCanvasCoordinates(e.clientX, e.clientY);
      const dx = raw.x - session.origin.x;
      const dy = raw.y - session.origin.y;
      const threshold = WALL_DRAG_THRESHOLD_PX / Math.max(canvasZoom, 0.25);
      if (!session.started) {
        if (Math.hypot(dx, dy) < threshold) return;
        session.started = true;
        skipNextWallClickRef.current = true;
        snapshotFloorPlanForDrag();
        setIsWallDragActive(true);
        selectFloorPlanWall(session.wallId, false);
      }
      session.lastDelta = { x: dx, y: dy };
      session.lastFreeAngle = e.altKey;
      const next = applyConnectedWallDragToPlan(session.startPlan, {
        mode: session.mode,
        wallId: session.wallId,
        vertexPoint: session.vertexPoint,
        delta: maybeSnapDragDelta(session, session.lastDelta),
        freeAngle: session.lastFreeAngle,
        extend: wallDragMode === 'extend',
        preview: true,
      });
      if (next) {
        if (livePlanRafRef.current) cancelAnimationFrame(livePlanRafRef.current);
        livePlanRafRef.current = requestAnimationFrame(() => {
          livePlanRafRef.current = 0;
          useLiveFloorPlanStore.getState().setPlan(next);
        });
      }
    };

    const onUp = () => {
      const session = wallDragRef.current;
      if (!session) return;
      wallDragRef.current = null;
      if (livePlanRafRef.current) {
        cancelAnimationFrame(livePlanRafRef.current);
        livePlanRafRef.current = 0;
      }
      if (!session.started) {
        useLiveFloorPlanStore.getState().setPlan(null);
        return;
      }
      setIsWallDragActive(false);
      const committed = applyConnectedWallDragToPlan(session.startPlan, {
        mode: session.mode,
        wallId: session.wallId,
        vertexPoint: session.vertexPoint,
        delta: maybeSnapDragDelta(session, session.lastDelta),
        freeAngle: session.lastFreeAngle,
        extend: wallDragMode === 'extend',
        preview: false,
      });
      if (committed) {
        applyFloorPlanLive(committed);
        requestAnimationFrame(() => {
          useLiveFloorPlanStore.getState().setPlan(null);
        });
      } else {
        useLiveFloorPlanStore.getState().setPlan(null);
      }
      if (
        !committed ||
        JSON.stringify(committed.walls) === JSON.stringify(session.startPlan.walls)
      ) {
        discardLastFloorPlanSnapshot();
      }
      window.setTimeout(() => {
        skipNextWallClickRef.current = false;
      }, 0);
    };

    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    };
  }, [
    applyFloorPlanLive,
    canvasZoom,
    discardLastFloorPlanSnapshot,
    getCanvasCoordinates,
    selectFloorPlanWall,
    snapshotFloorPlanForDrag,
    wallDragMode,
  ]);

  useEffect(() => {
    migrateLegacyFloorplanIntoDesign();
  }, [migrateLegacyFloorplanIntoDesign]);

  useEffect(() => {
    return () => {
      if (livePlanRafRef.current) cancelAnimationFrame(livePlanRafRef.current);
      useLiveFloorPlanStore.getState().setPlan(null);
      useLiveFloorPlanStore.getState().setHoverRoomPreview(null);
    };
  }, []);

  const resolvePoint = useCallback(
    (raw: FloorPlanPoint, from?: FloorPlanPoint | null): DraftSnapResult => {
      const fp = getFloorPlan();
      const walls = fp?.walls || [];
      const { enabled, gridSize } = wallsSnapToGridEnabled();
      const cursor = enabled ? snapDraftPointToGrid(from ?? undefined, raw, gridSize, altFreeAngle) : raw;
      if (from) {
        const result = snapDraftEndpoint(
          from,
          cursor,
          walls,
          altFreeAngle,
          undefined,
          endpointSnapPx
        );
        if (result.kind === 'endpoint' || result.kind === 'edge') return result;
        if (enabled) {
          return {
            ...result,
            point: snapDraftPointToGrid(from, result.point, gridSize, altFreeAngle),
            kind: result.kind ?? 'grid',
            gridSnapped: true,
          };
        }
        return result;
      }
      const start = snapPointDetailed(cursor, walls, endpointSnapPx);
      if (start.kind === 'endpoint' || start.kind === 'edge') return start;
      if (enabled) {
        return {
          ...start,
          point: snapDraftPointToGrid(undefined, start.point, gridSize, true),
          kind: start.kind ?? 'grid',
          gridSnapped: true,
        };
      }
      return start;
    },
    [altFreeAngle, endpointSnapPx, getFloorPlan]
  );

  const commitWall = useCallback(
    (start: FloorPlanPoint, end: FloorPlanPoint) => {
      const dist = Math.hypot(end.x - start.x, end.y - start.y);
      if (dist < 2) return null;

      const fp = ensureFloorPlan();
      const typed = lengthInput.trim() ? parseFloat(lengthInput) : NaN;
      const hasTypedLength = Number.isFinite(typed) && typed > 0;
      // Typed length is always plan meters / feet (e.g. 5 or 0.5) — screen just scales it down
      const unit = fp.units === 'imperial' ? 'ft' : 'm';

      let finalEnd = end;
      if (hasTypedLength) {
        const meters = lengthToMeters(typed, unit);
        // Fixed display scale: 50 px = 1 m (5 m → 250 px on canvas)
        const targetPx = meters * DEFAULT_PIXELS_PER_METER;
        const angle = Math.atan2(end.y - start.y, end.x - start.x);
        finalEnd = {
          x: start.x + Math.cos(angle) * targetPx,
          y: start.y + Math.sin(angle) * targetPx,
        };
      }

      const id = addWall({ a: start, b: finalEnd });
      setLengthInput('');
      return { id, end: finalEnd };
    },
    [addWall, ensureFloorPlan, lengthInput]
  );

  const handleFloorPlanClick = useCallback(
    (e: React.MouseEvent): boolean => {
      if (
        canvasMode !== 'wall' &&
        canvasMode !== 'room' &&
        canvasMode !== 'door' &&
        canvasMode !== 'measure' &&
        canvasMode !== 'select'
      ) {
        return false;
      }

      // Don't steal clicks from UI overlays
      const target = e.target as HTMLElement;
      if (target.closest('button, input, select, textarea, [data-floorplan-ui]')) {
        return false;
      }

      const raw = getCanvasCoordinates(e.clientX, e.clientY);
      const fp = ensureFloorPlan();

      if (canvasMode === 'select') {
        if (consumeWallDragClick() || wallDragRef.current) {
          return true;
        }
        // Walls / rooms / openings handle their own clicks. Empty canvas deselects.
        if (
          target.closest(
            '[data-wall-id], [data-wall-handle], [data-floorplan-room], [data-floorplan-opening]'
          )
        ) {
          return false;
        }
        selectFloorPlanWall(null);
        setSelectedFloorPlanRoomId(null);
        setSelectedFloorPlanOpeningId(null);
        return false;
      }

      if (canvasMode === 'door') {
        const nearest = findNearestWall(raw, fp.walls, 28);
        if (
          nearest &&
          doorFitsOnWall(nearest.wall, DEFAULT_DOOR_WIDTH_M, DEFAULT_PIXELS_PER_METER)
        ) {
          const t = clampDoorT(
            nearest.wall,
            DEFAULT_DOOR_WIDTH_M,
            nearest.t,
            DEFAULT_PIXELS_PER_METER
          );
          addOpening({
            wallId: nearest.wall.id,
            t,
            widthM: DEFAULT_DOOR_WIDTH_M,
            kind: 'door',
            hinge: doorSwing.hinge,
            swingSide: doorSwing.swingSide,
          });
        }
        return true;
      }

      if (canvasMode === 'room') {
        // Don't open create prompt when clicking an existing room
        if (fp.rooms.some((r) => r.polygon.length >= 3 && pointInPolygon(raw, r.polygon))) {
          return true;
        }
        const loop = findClosedLoopAtPoint(raw, fp.walls);
        if (loop) {
          setRoomNamePrompt({ mode: 'create', ...loop });
          setRoomName('');
        }
        return true;
      }

      if (canvasMode === 'wall' || canvasMode === 'measure') {
        if (canvasMode === 'measure' && measureLocked) {
          // Start a new measurement
          const start = resolvePoint(raw);
          setDraftStart(start.point);
          setDraftCurrent(start.point);
          setDraftSnap(start);
          setMeasureLocked(false);
          return true;
        }
        if (!draftStart) {
          const start = resolvePoint(raw);
          setDraftStart(start.point);
          setDraftCurrent(start.point);
          setDraftSnap(start);
          setLengthInput('');
          setMeasureLocked(false);
        } else {
          const end = resolvePoint(raw, draftStart);
          if (canvasMode === 'wall') {
            const result = commitWall(draftStart, end.point);
            if (result) {
              // Chain walls from the end point
              setDraftStart(result.end);
              setDraftCurrent(result.end);
              setDraftSnap(null);
            } else {
              setDraftStart(null);
              setDraftCurrent(null);
              setDraftSnap(null);
            }
          } else {
            // Measure: lock the segment; next click starts a new one
            setDraftCurrent(end.point);
            setDraftSnap(end);
            setMeasureLocked(true);
          }
        }
        return true;
      }

      return false;
    },
    [
      addOpening,
      canvasMode,
      commitWall,
      doorSwing,
      draftStart,
      ensureFloorPlan,
      getCanvasCoordinates,
      resolvePoint,
      consumeWallDragClick,
      selectFloorPlanWall,
      setSelectedFloorPlanRoomId,
      setSelectedFloorPlanOpeningId,
      measureLocked,
    ]
  );

  const updateHoverRoomPreview = useCallback(
    (raw: FloorPlanPoint) => {
      const setHover = useLiveFloorPlanStore.getState().setHoverRoomPreview;
      const current = useLiveFloorPlanStore.getState().hoverRoomPreview;
      const clearHover = () => {
        if (current) setHover(null);
      };
      // Highlight unclaimed closed areas only while placing rooms
      if (canvasMode !== 'room') {
        clearHover();
        return;
      }
      const fp = getFloorPlan();
      if (!fp?.walls.length) {
        clearHover();
        return;
      }
      // Already inside a named room — don't treat as "new"
      if (fp.rooms.some((r) => r.polygon.length >= 3 && pointInPolygon(raw, r.polygon))) {
        clearHover();
        return;
      }
      const loop = findClosedLoopAtPoint(raw, fp.walls);
      if (!loop) {
        clearHover();
        return;
      }
      // Skip if this loop is already registered as a room (same walls)
      const sig = [...loop.wallIds].sort().join(',');
      const already = fp.rooms.some(
        (r) => [...r.wallIds].sort().join(',') === sig
      );
      if (already) {
        clearHover();
        return;
      }
      if (current && [...current.wallIds].sort().join(',') === sig) return;
      setHover(loop);
    },
    [canvasMode, getFloorPlan]
  );

  const updateDoorPreview = useCallback(
    (raw: FloorPlanPoint) => {
      if (canvasMode !== 'door') {
        setDoorPreview(null);
        return;
      }
      const fp = getFloorPlan();
      if (!fp?.walls.length) {
        setDoorPreview(null);
        return;
      }
      const nearest = findNearestWall(raw, fp.walls, 28);
      if (!nearest) {
        setDoorPreview(null);
        return;
      }
      const widthM = DEFAULT_DOOR_WIDTH_M;
      const fits = doorFitsOnWall(nearest.wall, widthM, DEFAULT_PIXELS_PER_METER);
      const t = clampDoorT(nearest.wall, widthM, nearest.t, DEFAULT_PIXELS_PER_METER);
      setDoorPreview({
        wallId: nearest.wall.id,
        t,
        widthM,
        fits,
        hinge: doorSwing.hinge,
        swingSide: doorSwing.swingSide,
      });
    },
    [canvasMode, doorSwing, getFloorPlan]
  );

  const handleFloorPlanMouseMove = useCallback(
    (e: React.MouseEvent): boolean => {
      if (wallDragRef.current?.started || isWallDragActive) {
        return true;
      }
      const raw = getCanvasCoordinates(e.clientX, e.clientY);
      updateHoverRoomPreview(raw);
      updateDoorPreview(raw);

      if (canvasMode === 'wall') {
        const walls = getFloorPlan()?.walls || [];
        // Show nearby endpoints as magnets (and all endpoints while drafting)
        if (walls.length) {
          const all = collectUniqueEndpoints(walls);
          if (draftStart) {
            setSnapMagnets(all);
          } else {
            const near = all.filter(
              (p) =>
                Math.hypot(p.x - raw.x, p.y - raw.y) < endpointSnapPx * 2.5
            );
            setSnapMagnets(near);
          }
        } else {
          setSnapMagnets([]);
        }
        // Live cursor at the snapped start point (grid / endpoint) before the first click
        if (!draftStart) {
          const preview = resolvePoint(raw);
          setDraftSnap(preview.kind ? preview : null);
        }
      } else if (snapMagnets.length) {
        setSnapMagnets([]);
      }

      if (canvasMode === 'door') {
        return true;
      }

      if ((canvasMode !== 'wall' && canvasMode !== 'measure') || !draftStart || measureLocked) {
        return false;
      }
      const snapped = resolvePoint(raw, draftStart);
      setDraftCurrent(snapped.point);
      setDraftSnap(snapped);
      return true;
    },
    [
      canvasMode,
      draftStart,
      endpointSnapPx,
      getCanvasCoordinates,
      getFloorPlan,
      isWallDragActive,
      measureLocked,
      resolvePoint,
      snapMagnets.length,
      updateDoorPreview,
      updateHoverRoomPreview,
    ]
  );

  const cancelDraft = useCallback(() => {
    setDraftStart(null);
    setDraftCurrent(null);
    setDraftSnap(null);
    setSnapMagnets([]);
    setLengthInput('');
    setMeasureLocked(false);
  }, []);

  const confirmRoomName = useCallback(() => {
    if (!roomNamePrompt || !roomName.trim()) {
      setRoomNamePrompt(null);
      return;
    }
    if (roomNamePrompt.mode === 'rename' && roomNamePrompt.roomId) {
      updateRoom(roomNamePrompt.roomId, { name: roomName.trim() });
    } else {
      addRoom({
        name: roomName.trim(),
        polygon: roomNamePrompt.polygon,
        wallIds: roomNamePrompt.wallIds,
      });
    }
    setRoomNamePrompt(null);
    setRoomName('');
  }, [addRoom, roomName, roomNamePrompt, updateRoom]);

  const startRenameRoom = useCallback(
    (roomId: string) => {
      const fp = getFloorPlan();
      const room = fp?.rooms.find((r) => r.id === roomId);
      if (!room) return;
      setRoomNamePrompt({
        mode: 'rename',
        roomId: room.id,
        polygon: room.polygon,
        wallIds: room.wallIds,
      });
      setRoomName(room.name);
      setSelectedFloorPlanRoomId(roomId);
    },
    [getFloorPlan, setSelectedFloorPlanRoomId]
  );

  // Keyboard shortcuts
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement)?.tagName;
      const typing =
        tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || (e.target as HTMLElement)?.isContentEditable;

      if (e.key === 'Alt') setAltFreeAngle(true);

      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'z') {
        e.preventDefault();
        if (e.shiftKey) redoFloorPlan();
        else undoFloorPlan();
        return;
      }
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'y') {
        e.preventDefault();
        redoFloorPlan();
        return;
      }

      if (typing) {
        // Enter commits length while drafting a wall
        if (e.key === 'Enter' && draftStart && draftCurrent && canvasMode === 'wall' && lengthInput) {
          e.preventDefault();
          const result = commitWall(draftStart, draftCurrent);
          if (result) {
            setDraftStart(result.end);
            setDraftCurrent(result.end);
          }
        }
        return;
      }

      if (e.key === 'Escape') {
        cancelDraft();
        setRoomNamePrompt(null);
        useProjectStore.getState().cancelPendingReferenceImages();
        setCanvasMode('place');
        return;
      }

      if (e.key === 'Delete' || e.key === 'Backspace') {
        const selectedReferenceImageId = useProjectStore.getState().selectedReferenceImageId;
        if (selectedReferenceImageId) {
          useProjectStore.getState().removeFloorPlanReferenceImage(selectedReferenceImageId);
        } else if (selectedFloorPlanOpeningId) deleteOpening(selectedFloorPlanOpeningId);
        else if (selectedFloorPlanRoomId) deleteRoom(selectedFloorPlanRoomId);
        else if (selectedFloorPlanWallIds.length > 0) deleteWalls(selectedFloorPlanWallIds);
        else if (selectedFloorPlanWallId) deleteWall(selectedFloorPlanWallId);
        return;
      }

      const key = e.key.toLowerCase();

      // In door mode (or with a door selected), R cycles swing hinge/side
      if (key === 'r' && (canvasMode === 'door' || selectedFloorPlanOpeningId)) {
        e.preventDefault();
        if (canvasMode === 'door') {
          setDoorSwing((prev) => nextDoorSwing(prev.hinge, prev.swingSide));
        }
        if (selectedFloorPlanOpeningId) {
          const fp = getFloorPlan();
          const opening = fp?.openings.find((o) => o.id === selectedFloorPlanOpeningId);
          if (opening?.kind === 'door') {
            const next = nextDoorSwing(opening.hinge ?? 'start', opening.swingSide ?? 1);
            updateOpening(selectedFloorPlanOpeningId, next);
          }
        }
        return;
      }

      if (key === 'v') setCanvasMode(canvasMode === 'select' ? 'place' : 'select');
      else if (key === 'w') {
        setCanvasMode(canvasMode === 'wall' ? 'place' : 'wall');
        cancelDraft();
      } else if (key === 'r') setCanvasMode(canvasMode === 'room' ? 'place' : 'room');
      else if (key === 'd') setCanvasMode(canvasMode === 'door' ? 'place' : 'door');
      else if (key === 'm') {
        setCanvasMode(canvasMode === 'measure' ? 'place' : 'measure');
        cancelDraft();
      }

      // Typed length while drafting (digits / decimal)
      if (
        draftStart &&
        canvasMode === 'wall' &&
        ((key >= '0' && key <= '9') || key === '.' || key === ',')
      ) {
        setLengthInput((prev) => prev + (key === ',' ? '.' : key));
      }
      if (draftStart && canvasMode === 'wall' && e.key === 'Backspace' && lengthInput) {
        setLengthInput((prev) => prev.slice(0, -1));
      }
      if (draftStart && canvasMode === 'wall' && e.key === 'Enter' && draftCurrent) {
        e.preventDefault();
        const result = commitWall(draftStart, draftCurrent);
        if (result) {
          setDraftStart(result.end);
          setDraftCurrent(result.end);
        }
      }
    };

    const onKeyUp = (e: KeyboardEvent) => {
      if (e.key === 'Alt') setAltFreeAngle(false);
    };

    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
    };
  }, [
    cancelDraft,
    canvasMode,
    commitWall,
    deleteOpening,
    deleteWalls,
    deleteRoom,
    deleteWall,
    draftCurrent,
    draftStart,
    getFloorPlan,
    lengthInput,
    redoFloorPlan,
    selectedFloorPlanOpeningId,
    selectedFloorPlanRoomId,
    selectedFloorPlanWallId,
    selectedFloorPlanWallIds,
    setCanvasMode,
    undoFloorPlan,
    updateOpening,
  ]);

  // Clear draft when leaving wall/measure modes
  useEffect(() => {
    if (canvasMode !== 'wall' && canvasMode !== 'measure') {
      cancelDraft();
    }
  }, [canvasMode, cancelDraft]);

  useEffect(() => {
    if (canvasMode !== 'room' && canvasMode !== 'select') {
      useLiveFloorPlanStore.getState().setHoverRoomPreview(null);
    }
    if (canvasMode !== 'wall') {
      setSnapMagnets([]);
      if (!draftStart) setDraftSnap(null);
    }
    if (canvasMode !== 'door') {
      setDoorPreview(null);
    }
  }, [canvasMode, draftStart]);

  // Keep live preview in sync when swing orientation changes (R)
  useEffect(() => {
    setDoorPreview((prev) =>
      prev
        ? { ...prev, hinge: doorSwing.hinge, swingSide: doorSwing.swingSide }
        : prev
    );
  }, [doorSwing]);

  const commitTypedWallLength = useCallback(
    (wallId: string, raw: string) => {
      const fp = getFloorPlan();
      if (!fp) return false;
      const cleaned = raw.trim().replace(/,/g, '.').replace(/\s*(m|ft|feet)\s*$/i, '');
      const parsed = parseFloat(cleaned);
      if (!Number.isFinite(parsed) || parsed <= 0) return false;
      const meters = lengthToMeters(parsed, fp.units === 'imperial' ? 'ft' : 'm');
      const next = applyWallLengthToPlan(fp, wallId, meters);
      if (!next) return false;
      if (JSON.stringify(next.walls) === JSON.stringify(fp.walls)) return true;
      snapshotFloorPlanForDrag();
      applyFloorPlanLive(next);
      return true;
    },
    [applyFloorPlanLive, getFloorPlan, snapshotFloorPlanForDrag]
  );

  return {
    draftStart,
    draftCurrent,
    draftSnap,
    snapMagnets,
    hoverRoomPreview: null,
    doorPreview,
    doorSwing,
    lengthInput,
    setLengthInput,
    roomNamePrompt,
    roomName,
    setRoomName,
    confirmRoomName,
    cancelRoomName: () => {
      setRoomNamePrompt(null);
      setRoomName('');
    },
    startRenameRoom,
    handleFloorPlanClick,
    handleFloorPlanMouseMove,
    handleWallPointerDown,
    consumeWallDragClick,
    isWallDragActive,
    commitTypedWallLength,
    cancelDraft,
  };
}
